import { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { getVehicleOverview } from '../api';
import type { VehicleOverview, OverviewPeriod } from '../api';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { kmToDisplay, distanceUnitLabel } from '../utils/units';

interface Props {
  vehicleId: number;
}

type RowEntry =
  | { kind: 'section'; label: string; showToggle?: boolean }
  | { kind: 'subsection'; label: string }
  | { kind: 'data'; label: string; ytd: string; sl: string };

export default function OverviewTab({ vehicleId }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [data, setData] = useState<VehicleOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [exclVat, setExclVat] = useState(true);

  useEffect(() => {
    setLoading(true);
    getVehicleOverview(vehicleId)
      .then(setData)
      .finally(() => setLoading(false));
  }, [vehicleId]);

  if (loading) return <div className="p-12 text-center text-gray-400">{t('overview.loading')}</div>;
  if (!data) return <div className="p-12 text-center text-gray-400">{t('overview.no_data')}</div>;

  const fmt    = (n: number | null | undefined, d = 2) => n == null ? '—' : formatNumber(n, settings.numberFormat, d);
  const fmtCur = (n: number | null | undefined) => n == null ? '—' : `${formatNumber(n, settings.numberFormat, 2)} ${settings.currency}`;
  const fmtKm  = (km: number | null | undefined) => {
    if (km == null) return '—';
    const v = kmToDisplay(km, settings.distanceUnit);
    return v == null ? '—' : `${formatNumber(v, settings.numberFormat, 0)} ${distanceUnitLabel(settings.distanceUnit)}`;
  };
  const fmtInt   = (n: number | null | undefined) => n == null ? '—' : formatNumber(n, settings.numberFormat, 0);
  const fmtHours = (h: number | null | undefined) => h == null ? '—' : `${formatNumber(h, settings.numberFormat, 1)} ${t('overview.hours_unit')}`;

  // Vælg brutto eller excl. moms — bruges til alle pengetal og afledte nøgletal
  const earn = (p: OverviewPeriod) => exclVat ? p.earnings_excl_vat : p.earnings;
  const exp  = (p: OverviewPeriod) => exclVat ? p.expenses_excl_vat : p.expenses;
  const net  = (p: OverviewPeriod) => exclVat ? p.net_excl_vat      : p.net;
  const perRental = (p: OverviewPeriod) => p.rental_count > 0 ? earn(p) / p.rental_count : null;
  const perKm     = (p: OverviewPeriod) => p.total_km     > 0 ? earn(p) / p.total_km     : null;
  const perDay    = (p: OverviewPeriod) => p.days_online  > 0 ? earn(p) / p.days_online  : null;
  const perWeek   = (p: OverviewPeriod) => p.weeks_online > 0 ? earn(p) / p.weeks_online : null;
  const perMonth  = (p: OverviewPeriod) => p.weeks_online > 0 ? (earn(p) / p.weeks_online) * 52 / 12 : null;

  const ytd = data.ytd;
  const sl  = data.since_listed;
  const showSinceListed = sl !== null;

  const ytdCur = (fn: (p: OverviewPeriod) => number | null) => fmtCur(fn(ytd));
  const slCur  = (fn: (p: OverviewPeriod) => number | null) => sl ? fmtCur(fn(sl)) : '—';

  const rows: RowEntry[] = [
    { kind: 'section', label: t('overview.section_activity') },
    { kind: 'data', label: t('overview.days_online'),   ytd: fmtInt(ytd.days_online),   sl: sl ? fmtInt(sl.days_online)   : '—' },
    { kind: 'data', label: t('overview.days_offline'),  ytd: fmtInt(ytd.days_offline),  sl: sl ? fmtInt(sl.days_offline)  : '—' },
    { kind: 'data', label: t('overview.weeks_online'),  ytd: fmt(ytd.weeks_online),     sl: sl ? fmt(sl.weeks_online)     : '—' },
    { kind: 'data', label: t('overview.rental_count'),  ytd: fmtInt(ytd.rental_count),  sl: sl ? fmtInt(sl.rental_count)  : '—' },
    { kind: 'data', label: t('overview.total_km'),      ytd: fmtKm(ytd.total_km),       sl: sl ? fmtKm(sl.total_km)       : '—' },
    { kind: 'data', label: t('overview.time_hours'),    ytd: fmtHours(ytd.time_hours),  sl: sl ? fmtHours(sl.time_hours)  : '—' },

    { kind: 'section', label: t('overview.section_economy'), showToggle: true },
    { kind: 'data', label: t('overview.earnings'),      ytd: fmtCur(earn(ytd)),         sl: sl ? fmtCur(earn(sl))         : '—' },
    { kind: 'data', label: t('overview.expenses'),      ytd: fmtCur(exp(ytd)),          sl: sl ? fmtCur(exp(sl))          : '—' },
    { kind: 'data', label: t('overview.net'),           ytd: fmtCur(net(ytd)),          sl: sl ? fmtCur(net(sl))          : '—' },

    { kind: 'section',    label: t('overview.section_kpis'), showToggle: true },
    { kind: 'subsection', label: t('overview.section_kpis_per_rental') },
    { kind: 'data', label: t('overview.per_rental'),    ytd: ytdCur(perRental),         sl: slCur(perRental) },
    { kind: 'data', label: t('overview.km_per_rental'), ytd: fmtKm(ytd.km_per_rental),  sl: sl ? fmtKm(sl.km_per_rental)  : '—' },

    { kind: 'subsection', label: t('overview.section_kpis_per_unit') },
    { kind: 'data', label: t('overview.per_km'),        ytd: ytdCur(perKm),             sl: slCur(perKm) },
    { kind: 'data', label: t('overview.per_day'),       ytd: ytdCur(perDay),            sl: slCur(perDay) },
    { kind: 'data', label: t('overview.per_week'),      ytd: ytdCur(perWeek),           sl: slCur(perWeek) },
    { kind: 'data', label: t('overview.per_month'),     ytd: ytdCur(perMonth),          sl: slCur(perMonth) },
  ];

  const colCount = showSinceListed ? 3 : 2;

  return (
    <div className="p-6">
      {!showSinceListed && (
        <p className="text-xs text-[var(--color-warning)] mb-4">{t('overview.no_listed_on_hint')}</p>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-2 font-medium text-gray-700"></th>
            <th className="py-2 font-medium text-gray-700 text-right">{t('overview.col_ytd')}</th>
            {showSinceListed && (
              <th className="py-2 font-medium text-gray-700 text-right">{t('overview.col_since_listed')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <Fragment key={i}>
              {row.kind === 'section' && (
                <tr>
                  <td colSpan={colCount} className="pt-6 pb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-[var(--color-sage-700)]">
                        {row.label}
                      </span>
                      {row.showToggle && (
                        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-xs">
                          <button
                            type="button"
                            onClick={() => setExclVat(true)}
                            className={`px-3 py-1.5 rounded-md transition-colors ${
                              exclVat ? 'bg-[var(--color-sage-100)] text-[var(--color-sage-900)] font-medium' : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {t('overview.vat_excl')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setExclVat(false)}
                            className={`px-3 py-1.5 rounded-md transition-colors ${
                              !exclVat ? 'bg-[var(--color-sage-100)] text-[var(--color-sage-900)] font-medium' : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {t('overview.vat_incl')}
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {row.kind === 'subsection' && (
                <tr>
                  <td colSpan={colCount} className="pt-3 pb-1 text-xs font-medium text-gray-500">
                    {row.label}
                  </td>
                </tr>
              )}
              {row.kind === 'data' && (
                <tr className="border-b border-gray-50">
                  <td className="py-2 text-gray-700">{row.label}</td>
                  <td className="py-2 text-right tabular-nums">{row.ytd}</td>
                  {showSinceListed && <td className="py-2 text-right tabular-nums">{row.sl}</td>}
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
